import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';
import { createIdentifierFromQuery } from '@confluence/graphql/entry-points/createIdentifierFromQuery.ts';
import { getClientStream } from '@confluence/ssr-data-streaming/entry-points/client.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import SpaceNavigationQueryArtifact, {
	type SpaceNavigationQuery$variables,
} from './__generated__/SpaceNavigationQuery.graphql.ts';
import { getOrCreateSpaceNavigationQueryOwner } from './SpaceNavigationQueryOwner.ts';

declare global {
	interface Window {
		__SPACE_NAVIGATION_QUERY_VARIABLES__?: SpaceNavigationQuery$variables;
	}
}

type RelayStreamEvent = {
	type: string;
	operation: string;
	variables: Record<string, unknown>;
};

/**
 * Called during browser bootstrap after the Relay environment and Statsig are initialized.
 * SSR transfers records and variables; query references always belong to the client environment.
 */
export function initializeSpaceNavigationQueryHydration(): void {
	if (
		StatsigConfigurations.isKillSwitchOn('confluence_space_nav_preloaded_query') ||
		(!expVal('confluence_exp_space_nav_contract', 'isEnabled', false) &&
			!expVal('confluence_exp_space_nav_combined_links', 'isEnabled', false))
	) {
		return;
	}
	const environment = getRelayEnvironment();
	const owner = getOrCreateSpaceNavigationQueryOwner(environment);

	if (window.__SPACE_NAVIGATION_QUERY_VARIABLES__) {
		owner.preload(window.__SPACE_NAVIGATION_QUERY_VARIABLES__);
	}
	if (!window.__SSR_REACT_STREAMING__) return;

	// The existing Relay network listener caches each response before this listener runs.
	// loadQuery can therefore consume the streamed response without a browser request, even
	// when the initial __RELAY_STORE__ snapshot preceded this boundary's response.
	const { text, id } = SpaceNavigationQueryArtifact.params;
	const operation = text ? createIdentifierFromQuery(text) : `id:${id}`;
	const stream = getClientStream<RelayStreamEvent>('relay');
	const onResult = (event: RelayStreamEvent) => {
		if (event.type !== 'result' || event.operation !== operation) return;
		const { cloudId, spaceKey, isLicensed } = event.variables;
		if (
			typeof cloudId !== 'string' ||
			typeof spaceKey !== 'string' ||
			typeof isLicensed !== 'boolean'
		) {
			return;
		}
		owner.preload({ ...event.variables, cloudId, spaceKey, isLicensed });
	};
	stream.addListener(onResult);
	owner.setHydrationCleanup(() => stream.removeListener(onResult));
}
